import { ReactNode, useCallback, useMemo, useRef } from 'react';

import { Cell, Row, flexRender } from '@tanstack/react-table';
import { MantineTable, MantineTableProps, cx } from 'ui';

import { ACTION_MENU_TARGET_ID } from '../../config/constants';
import { useTableContext } from '../../table.context';
import { getCommonCellStyles } from '../utils';
import classes from './TableBody.module.css';
import { RowProvider } from './row/RowProvider';
import { useContextMenu } from './row/useContextMenu';

const getActionMenuElement = (tableRowRef: HTMLTableRowElement | null): HTMLDivElement | null => {
  return tableRowRef?.querySelector(`.${ACTION_MENU_TARGET_ID}`) ?? null;
};

/**
 * Table Row Component
 */
const TableRows = ({ row, tableRowClassId }: { row: Row<unknown>; tableRowClassId?: string }) => {
  const { table } = useTableContext();
  const tableRowRef = useRef<HTMLTableRowElement>(null);
  /** Ref to the action column menu */
  const isPdf = useMemo(() => table.options.meta?.isPdf, [table.options.meta?.isPdf]);

  const { rightClickHandler, isOpenedViaContextMenu } = useContextMenu();

  const clickActionMenu = useCallback(() => {
    const actionMenuElement = getActionMenuElement(tableRowRef.current);
    actionMenuElement?.click();
  }, []);

  /**
   * Detects if the context menu is open due to the row action menu or the context menu
   */
  const isContextMenuOpen = useMemo(() => {
    const actionMenuElement = getActionMenuElement(tableRowRef.current);
    const isElementOpened = actionMenuElement?.getAttribute('data-expanded') === 'true';
    return isOpenedViaContextMenu || isElementOpened;
  }, [isOpenedViaContextMenu]);

  return (
    <MantineTable.Tr
      className={cx([classes.tr, tableRowClassId])}
      onContextMenu={(e) => {
        if (isContextMenuOpen) return;
        rightClickHandler(e, clickActionMenu);
      }}
      ref={tableRowRef}
    >
      {row.getVisibleCells().map((cell: Cell<unknown, unknown>) => {
        // isPrintable doesn't contain a default, so we need to explicitly check for false
        if (isPdf && cell.column?.columnDef?.meta?.isPrintable === false) return null;

        // isRenderable doesn't contain a default, so we need to explicitly check for false
        if (cell.column?.columnDef?.meta?.isRenderable === false && !isPdf) return null;

        const { styles } = getCommonCellStyles({ column: cell.column });
        return (
          <MantineTable.Td
            mod={{ pinned: cell.column.getIsPinned() ? true : false }}
            key={cell.id}
            className={cx(classes.td, cell.column.id)}
            style={{
              width: cell.column.getSize(),
              ...styles,
            }}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </MantineTable.Td>
        );
      })}
    </MantineTable.Tr>
  );
};

interface TableBodyProps extends MantineTableProps {
  children?: ReactNode | ReactNode[];
  // Used to apply a class to each table Row
  tableRowClassId?: string;
}

const TableBody = ({ children, tableRowClassId }: TableBodyProps) => {
  const ctx = useTableContext();

  if (children) return <>{children}</>;

  return (
    <MantineTable.Tbody>
      {ctx.table.getRowModel().rows.map((row: Row<unknown>) => {
        return (
          <RowProvider key={row.id}>
            <TableRows row={row} tableRowClassId={tableRowClassId} />
          </RowProvider>
        );
      })}
    </MantineTable.Tbody>
  );
};
export { TableBody };
export type { TableBodyProps };
